import { useCallback } from 'react'
import { useToast } from '@chakra-ui/react'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useNavigation } from '@/hooks/useNavigation'
import { useAppSelector } from '@/store'
import { isReadyForAiActions } from '@/store/slices/fileProcessingSlice'

export type AiAction = 'smartRename' | 'smartOrganize'

export interface AiActionScope {
  /** File ids to send. Empty means "everything the server resolves for this folder". */
  ids: number[]
  /** How many selected files were left out for still being processed. */
  skipped: number
}

/**
 * Narrow the explorer selection to the files the AI actions can actually reason about.
 *
 * Shared by the folder header and the root ("All Documents") header, which offer the same
 * two actions over different scopes.
 */
export function useAiActionScope(): (action: AiAction) => AiActionScope | null {
  const toast = useToast()
  const { t } = useAppTranslation(['files', 'common'])
  const fileProcessing = useAppSelector((state) => state.fileProcessing.byId)
  const { selectedFiles, folderFiles } = useNavigation()

  /**
   * Both actions read the document's extracted text; a file still being OCR'd has none and
   * would be renamed or filed off its name alone. Returns null when the caller should not
   * proceed at all — see the empty-scope note at the call sites.
   */
  return useCallback(
    (action: AiAction): AiActionScope | null => {
      const selected = Array.from(selectedFiles)

      // No selection means "everything in scope", which the server resolves and filters itself.
      if (selected.length === 0) return { ids: [], skipped: 0 }

      // Merged against the poll's results, not read straight off folderFiles: those rows are
      // a snapshot from when the folder loaded, so a file that finished while the user was
      // looking at it would still be refused here long after its spinner cleared.
      const isReady = (id: number) => {
        const file = folderFiles.find((candidate) => candidate.id === id)

        return file ? isReadyForAiActions(file, fileProcessing) : false
      }

      const ids = selected.filter(isReady)

      if (ids.length === 0) {
        toast({
          title: t(`${action}.noneReady`, { ns: 'files' }),
          description: t(`${action}.noneReadyDescription`, { ns: 'files' }),
          status: 'warning',
          duration: 6000,
          isClosable: true,
        })
        return null
      }

      return { ids, skipped: selected.length - ids.length }
    },
    [fileProcessing, folderFiles, selectedFiles, t, toast]
  )
}
